import JSZip from "jszip";

/**
 * Mete una imagen dentro de un .xlsx ya escrito por `xlsx-js-style`.
 *
 * La librería no sabe escribir imágenes (`addImage` es de SheetJS Pro), así que
 * la imagen se inyecta a mano en el zip que produce: la imagen, un drawing por
 * hoja y sus relaciones. Mismo mecanismo que la Lista de Raya de nómina
 * (`PreNomina/TablaPrenomina/TablaPrenomina.js`), pero generalizado: aquí el
 * logo es el de cada empresa, así que ni el tamaño ni la extensión se conocen
 * de antemano.
 *
 * Sirve para el logo y para cualquier otra imagen (el Estado de Resultados por
 * Periodo mete aquí su gráfica ya rasterizada). Limitación deliberada: **una
 * sola imagen por libro** — el drawing, la imagen y el rId están fijos en el
 * número 1. Para una segunda habría que numerarlos, y todavía no hace falta.
 */

// Excel mide en EMU: 1 px = 9525.
const EMU = 9525;

// Caja por omisión (la del logo), en píxeles, conservando la proporción. Quien
// mete algo que no es un logo — una gráfica, por ejemplo — pasa la suya.
const ANCHO_MAX = 200;
const ALTO_MAX = 75;

// `xlsx-js-style` ya declara el <Default> de estas extensiones en
// [Content_Types].xml, así que no hay que parchearlo. Cualquier otra (svg,
// webp...) se omite: más vale un Excel sin logo que uno que Excel no abra.
const EXTENSIONES_SOPORTADAS = ["png", "jpg", "jpeg", "gif", "bmp"];

// En una descarga masiva todos los documentos comparten el mismo logo: sin esto
// se decodificaría y se volvería a medir una vez por documento.
const cacheLogos = new Map();

const base64AUint8Array = (base64) => {
  const binario = window.atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
};

/**
 * Lee las dimensiones reales de la imagen para no deformarla. Si no se puede
 * cargar, se devuelve la caja completa.
 */
const medirImagen = (dataUrl) =>
  new Promise((resolve) => {
    try {
      const imagen = new Image();
      imagen.onload = () => resolve({ ancho: imagen.naturalWidth, alto: imagen.naturalHeight });
      imagen.onerror = () => resolve(null);
      imagen.src = dataUrl;
    } catch (error) {
      resolve(null);
    }
  });

const dimensionesEnCaja = (medida, anchoMax, altoMax) => {
  if (!medida || !medida.ancho || !medida.alto) return { ancho: anchoMax, alto: altoMax };
  const escala = Math.min(anchoMax / medida.ancho, altoMax / medida.alto, 1);
  return {
    ancho: Math.round(medida.ancho * escala),
    alto: Math.round(medida.alto * escala),
  };
};

const xmlDrawing = (anchoPx, altoPx, fila, columna, nombre) => {
  const cx = anchoPx * EMU;
  const cy = altoPx * EMU;
  const off = 2 * EMU;
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"'
    + ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">'
    + '<xdr:oneCellAnchor>'
    + `<xdr:from><xdr:col>${columna}</xdr:col><xdr:colOff>${off}</xdr:colOff>`
    + `<xdr:row>${fila}</xdr:row><xdr:rowOff>${off}</xdr:rowOff></xdr:from>`
    + `<xdr:ext cx="${cx}" cy="${cy}"/>`
    + '<xdr:pic><xdr:nvPicPr>'
    + `<xdr:cNvPr id="1" name="${nombre}" descr="${nombre}"/>`
    + '<xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr>'
    + '</xdr:nvPicPr><xdr:blipFill>'
    + '<a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rId1"/>'
    + '<a:stretch><a:fillRect/></a:stretch></xdr:blipFill>'
    + `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>`
    + '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>'
    + '</xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>';
};

/**
 * @param {ArrayBuffer|Blob} buffer  el .xlsx que escribió xlsx-js-style
 * @param {Object} logo          `empresa.logo` ({ base64Completa, base64, extension })
 *                               o cualquier imagen con esa misma forma
 * @param {Object} [opciones]    `{ fila, columna }` donde se ancla (0-indexado),
 *                               `hoja` (1-indexada, por omisión la 1ª), `nombre`
 *                               (el que Excel muestra en el panel de objetos) y
 *                               `anchoMax`/`altoMax` en píxeles (por omisión la
 *                               caja del logo, 200x75 — una gráfica necesita la
 *                               suya o sale ilegible).
 *                               La hoja destino no debe traer `_rels` propio; las
 *                               que escribe xlsx-js-style no lo traen.
 * @returns {Promise<Blob|null>} el .xlsx con la imagen, o null si no se pudo
 *          (extensión no soportada, logo ausente): el llamador se queda con el
 *          Excel sin la imagen, que es un respaldo válido.
 */
export const inyectarLogoEnExcel = async (buffer, logo, opciones = {}) => {
  const extension = (logo?.extension || "").toString().toLowerCase();
  const base64 = logo?.base64;
  if (!base64 || !EXTENSIONES_SOPORTADAS.includes(extension)) return null;

  try {
    const anchoMax = opciones.anchoMax || ANCHO_MAX;
    const altoMax = opciones.altoMax || ALTO_MAX;
    // La caja va en la llave: la misma imagen encajada en dos cajas distintas no
    // da las mismas dimensiones.
    const clave = `${logo.path || base64}|${anchoMax}x${altoMax}`;
    if (!cacheLogos.has(clave)) {
      cacheLogos.set(clave, (async () => {
        const bytes = base64AUint8Array(base64);
        const medida = await medirImagen(logo.base64Completa || `data:image/${extension};base64,${base64}`);
        return { bytes, ...dimensionesEnCaja(medida, anchoMax, altoMax) };
      })());
    }
    const { bytes, ancho, alto } = await cacheLogos.get(clave);

    const zip = await JSZip.loadAsync(buffer);
    const RELS = "http://schemas.openxmlformats.org/package/2006/relationships";
    const RELS_DOC = RELS.replace("/package/2006", "/officeDocument/2006");
    // createFolders:false deja el paquete igual que lo entrega SheetJS, sin
    // entradas de directorio que él no escribe.
    const OPC = { createFolders: false };
    const poner = (ruta, contenido) => zip.file(ruta, contenido, OPC);

    const archivoImagen = `image1.${extension}`;
    poner(`xl/media/${archivoImagen}`, bytes);

    const hoja = opciones.hoja || 1;
    poner(
      `xl/drawings/drawing1.xml`,
      xmlDrawing(ancho, alto, opciones.fila || 0, opciones.columna || 0, opciones.nombre || "Logo")
    );
    poner(
      `xl/drawings/_rels/drawing1.xml.rels`,
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + `<Relationships xmlns="${RELS}"><Relationship Id="rId1"`
      + ` Type="${RELS_DOC}/image" Target="../media/${archivoImagen}"/></Relationships>`
    );
    // La hoja no traía _rels propio, así que se crea con la única relación.
    poner(
      `xl/worksheets/_rels/sheet${hoja}.xml.rels`,
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + `<Relationships xmlns="${RELS}"><Relationship Id="rId1"`
      + ` Type="${RELS_DOC}/drawing" Target="../drawings/drawing1.xml"/></Relationships>`
    );

    // [Content_Types].xml ya trae el Default de la extensión de la imagen; sólo
    // falta el Override del drawing.
    const ct = await zip.file("[Content_Types].xml").async("string");
    poner(
      "[Content_Types].xml",
      ct.replace(
        "</Types>",
        '<Override PartName="/xl/drawings/drawing1.xml"'
        + ' ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>'
      )
    );

    // <drawing> va al final del worksheet (después de ignoredErrors, según el
    // esquema). La raíz ya declara xmlns:r, pero se comprueba por si acaso.
    const ruta = `xl/worksheets/sheet${hoja}.xml`;
    let xml = await zip.file(ruta).async("string");
    if (!/<worksheet[^>]*xmlns:r=/.test(xml)) {
      xml = xml.replace("<worksheet ", `<worksheet xmlns:r="${RELS_DOC}" `);
    }
    poner(ruta, xml.replace("</worksheet>", '<drawing r:id="rId1"/></worksheet>'));

    return zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  } catch (error) {
    console.error("No se pudo inyectar el logo en el Excel:", error);
    return null;
  }
};

export default inyectarLogoEnExcel;
